import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MB_LinearGradient } from '@/components/general/linear-gradient';
import { mbTextStyles } from '@/utils/general/style-utils';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { ProductSubscription, ProductSubscriptionAndroid, ProductSubscriptionIOS } from 'react-native-iap';
import { COLORS } from '../../../constants/colors';
import { textStyles } from '../../../constants/textStyles';
import { PaymentPageProps } from '../../../typesAndInterfaces/componentProps';
import { ComponentWrapper } from '../../helperComponents/componentWrapper/ComponentWrapper';
import { MobileHeaderProps } from '../../helperComponents/Header/MobileHeader';
import MB_MobilePurchase, { selectAndroidSubscriptionOffer } from '../../../mightyByteLibraries/MB_MobilePurchase';
import { mbShowToast } from '@/components/general/toast';
import { mbHidePopUp, mbShowPopUp, MB_PopUpTemplateComponents } from '@/components/general/pop-up';
import { CONSTANTS, ENABLED_SUBSCRIPTION_SKUS, STRING_CONSTANTS, STRIPE_SUBSCRIPTION_PLATFORM, WEB_ENABLED_SUBSCRIPTION_SKUS } from '../../../constants/constants';
import { useCheckPaymentStatus, useCreateStripeCheckoutSession, useCreateStripePortalSession, useGetStripePlans, useSyncStripeCheckoutSession } from '../../../hooks/paymentAndOrdersHooks';
import { MB_Button } from '@/components/general/button';
import { PaymentPageEventResponder } from './PaymentPageEventResponder';
import { PAYMENT_STATUS, StripePlan } from '../../../typesAndInterfaces/typesAndInterfaces';
import { useGetCurrentUserData } from '../../../hooks/userHooks';
import { useQueryClient } from '@tanstack/react-query';
import { USER_QUERY_KEYS } from '../../../constants/constants';
import { MB_customWindowForWeb } from '@/utils/general/custom-window-for-web';
import { usePaymentContext } from '../../../context/PaymentContextProvider';
import { MB_TextInput } from '@/components/general/text-input';
import Feather from 'react-native-vector-icons/Feather';
import { useAppyEducatorCode } from '../../../hooks/userHooks';

// Web (Stripe) price for a plan card, e.g. "$49.99". Stripe amounts are integer minor
// units; the browser locale formats the currency.
function formatStripePrice(plan: StripePlan): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: plan.currency.toUpperCase() }).format(plan.unitAmount / 100);
  } catch {
    return `${(plan.unitAmount / 100).toFixed(2)} ${plan.currency.toUpperCase()}`;
  }
}

// Same saving rule as the store version, computed from Stripe prices.
function getWebAnnualSavingsPercent(annual?: StripePlan, monthly?: StripePlan): number | undefined {
  if (!annual || !monthly || annual.currency !== monthly.currency || monthly.unitAmount <= 0) {
    return undefined;
  }
  const percent = Math.round((1 - annual.unitAmount / (monthly.unitAmount * 12)) * 100);
  return percent >= 1 ? percent : undefined;
}

// Base (recurring) pricing phase of the Android offer we would actually purchase (see
// selectAndroidSubscriptionOffer) — the LAST phase, so a free-trial/intro phase can never
// masquerade as the subscription price.
function getAndroidBasePricingPhase(subscription: ProductSubscriptionAndroid) {
  const phases = selectAndroidSubscriptionOffer(subscription)?.pricingPhases?.pricingPhaseList;
  return phases?.[phases.length - 1];
}

// Localized store price — never hardcoded. iOS exposes displayPrice directly; Android
// nests it under the base-plan offer. Returns undefined when the store hasn't loaded
// (sim / offline).
function getLocalizedSubscriptionPrice(subscription?: ProductSubscription): string | undefined {
  if (!subscription) {
    return undefined;
  }
  if (Platform.OS === 'android') {
    return getAndroidBasePricingPhase(subscription as ProductSubscriptionAndroid)?.formattedPrice;
  }
  return subscription.displayPrice;
}

// Numeric price + currency from store data, used only to compute the annual saving.
function getNumericPrice(subscription?: ProductSubscription): { amount: number; currency: string } | undefined {
  if (!subscription) {
    return undefined;
  }
  if (Platform.OS === 'android') {
    const phase = getAndroidBasePricingPhase(subscription as ProductSubscriptionAndroid);
    const amount = phase ? Number(phase.priceAmountMicros) / 1_000_000 : NaN;
    return Number.isFinite(amount) && amount > 0 ? { amount, currency: phase!.priceCurrencyCode } : undefined;
  }
  const ios = subscription as ProductSubscriptionIOS;
  return ios.price != null && ios.price > 0 ? { amount: ios.price, currency: ios.currency } : undefined;
}

// 'month' | 'year' from store metadata so copy never assumes a billing term.
function getBillingPeriod(subscription?: ProductSubscription): 'month' | 'year' | undefined {
  if (!subscription) {
    return undefined;
  }
  if (Platform.OS === 'android') {
    // ISO 8601 duration, e.g. 'P1M' / 'P1Y'
    const period = getAndroidBasePricingPhase(subscription as ProductSubscriptionAndroid)?.billingPeriod;
    return period === 'P1Y' ? 'year' : period === 'P1M' ? 'month' : undefined;
  }
  const unit = (subscription as ProductSubscriptionIOS).subscriptionPeriodUnitIOS;
  return unit === 'year' || unit === 'month' ? unit : undefined;
}

// Whole-percent saving of the annual plan vs 12 months of the monthly plan, computed
// strictly from store prices. undefined when either price is missing, currencies differ,
// or the saving rounds below 1%.
function getAnnualSavingsPercent(annual?: ProductSubscription, monthly?: ProductSubscription): number | undefined {
  const annualPrice = getNumericPrice(annual);
  const monthlyPrice = getNumericPrice(monthly);
  if (!annualPrice || !monthlyPrice || annualPrice.currency !== monthlyPrice.currency) {
    return undefined;
  }
  const percent = Math.round((1 - annualPrice.amount / (monthlyPrice.amount * 12)) * 100);
  return percent >= 1 ? percent : undefined;
}

const PaymentPage = ({ navigation, route }: PaymentPageProps) => {
  const isWeb = Platform.OS === 'web';
  const mobileHeaderOptions = useRef<MobileHeaderProps>({ showHeader: true, showBackArrow: true, pageTitle: 'Payment' });
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const { mutate: checkPaymentStatus, isLoading: isCheckPurchaseStatusLoading } = useCheckPaymentStatus();

  const { paymentStatus } = usePaymentContext();
  const { mutate: applyEducatorCode, isLoading: isApplyingCode, data: educatorCode } = useAppyEducatorCode();

  const [errorMessage, setErrorMessage] = useState('');
  const [code, setCode] = useState('');
  const [isRestoring, setIsRestoring] = useState(false);
  const [subscriptions, setSubscriptions] = useState<ProductSubscription[]>([]);
  // Plans on sale, in display order (ENABLED_SUBSCRIPTION_SKUS). With a single SKU the
  // page renders one card and no selector; the monthly code path stays intact.
  const enabledPlans = useMemo(
    () =>
      [
        { sku: CONSTANTS.PREMIUM_ANNUAL_SUBSCRIPTION_NAME, title: 'Annual', fallbackPeriod: 'year' as const },
        { sku: CONSTANTS.PREMIUM_MONTHLY_SUBSCRIPTION_NAME, title: 'Monthly', fallbackPeriod: 'month' as const },
      ]
        .filter(plan => ENABLED_SUBSCRIPTION_SKUS.includes(plan.sku))
        .sort((a, b) => ENABLED_SUBSCRIPTION_SKUS.indexOf(a.sku) - ENABLED_SUBSCRIPTION_SKUS.indexOf(b.sku)),
    []
  );
  const isSinglePlan = enabledPlans.length === 1;
  const [selectedSku, setSelectedSku] = useState<string>((isWeb ? WEB_ENABLED_SUBSCRIPTION_SKUS[0] : enabledPlans[0]?.sku) ?? CONSTANTS.PREMIUM_ANNUAL_SUBSCRIPTION_NAME);

  // ---- Web (Stripe Checkout) -------------------------------------------------
  // The web never talks to the stores: plans and prices come from the backend, Pay Now
  // redirects to Stripe Checkout, and Checkout redirects back here with ?checkout=…
  const { data: currentUserData } = useGetCurrentUserData();
  const queryClient = useQueryClient();
  const { data: stripePlans, isLoading: isStripePlansLoading, isError: isStripePlansError } = useGetStripePlans({ enabled: isWeb });
  const { mutate: createStripeCheckoutSession, isLoading: isCreatingCheckout } = useCreateStripeCheckoutSession();
  const { mutate: syncStripeCheckoutSession, isLoading: isSyncingCheckout } = useSyncStripeCheckoutSession();
  const { mutate: createStripePortalSession, isLoading: isCreatingPortal } = useCreateStripePortalSession();
  const [stripeSuccess, setStripeSuccess] = useState(false);
  const handledCheckoutSessionRef = useRef<string | undefined>(undefined);

  const webPlans = useMemo(() => {
    const titles: Record<string, string> = {
      [CONSTANTS.PREMIUM_ANNUAL_SUBSCRIPTION_NAME]: 'Annual',
      [CONSTANTS.PREMIUM_MONTHLY_SUBSCRIPTION_NAME]: 'Monthly',
    };
    return WEB_ENABLED_SUBSCRIPTION_SKUS.map(sku => {
      const plan = stripePlans?.find(candidate => candidate.productId === sku);
      return plan ? { ...plan, title: titles[sku] ?? sku } : undefined;
    }).filter((plan): plan is StripePlan & { title: string } => plan !== undefined);
  }, [stripePlans]);
  const isSingleWebPlan = webPlans.length === 1;
  const selectedWebPlan = webPlans.find(plan => plan.productId === selectedSku) ?? webPlans[0];
  const webAnnualSavingsPercent = isSingleWebPlan
    ? undefined
    : getWebAnnualSavingsPercent(
        webPlans.find(plan => plan.productId === CONSTANTS.PREMIUM_ANNUAL_SUBSCRIPTION_NAME),
        webPlans.find(plan => plan.productId === CONSTANTS.PREMIUM_MONTHLY_SUBSCRIPTION_NAME)
      );
  const isStripeSubscriber = currentUserData?.subscriptionPlatform === STRIPE_SUBSCRIPTION_PLATFORM;

  // Returning from Checkout: apply the session on the backend right away (the webhook
  // usually beat us, but this makes the success modal deterministic), refresh the
  // payment context and strip the query params so a reload does not replay it.
  useEffect(() => {
    if (!isWeb) {
      return;
    }
    const { checkout, session_id: sessionId } = route.params ?? {};
    if (checkout === 'success' && sessionId && handledCheckoutSessionRef.current !== sessionId) {
      handledCheckoutSessionRef.current = sessionId;
      syncStripeCheckoutSession(sessionId, {
        onSuccess: newPaymentStatus => {
          MB_MobilePurchase.requestPaymentStatusRefresh();
          // subscriptionPlatform/accessExpiresAt live on the user object: refetch so the
          // Manage button and premium gating update without a reload.
          queryClient.invalidateQueries([USER_QUERY_KEYS.getCurrentUsertData]);
          if (newPaymentStatus === PAYMENT_STATUS.paid) {
            setStripeSuccess(true);
          } else {
            mbShowToast({ type: 'info', text1: 'Payment pending', text2: 'Your subscription will activate once the payment completes.' });
          }
        },
        onError: error => {
          console.error('Error when syncing Stripe checkout session', error);
          MB_MobilePurchase.requestPaymentStatusRefresh();
          mbShowToast({ type: 'error', text1: 'Could not confirm payment', text2: 'If you were charged, your access will update shortly. Please refresh.' });
        },
      });
      navigation.setParams({ checkout: undefined, session_id: undefined });
    } else if (checkout === 'cancel') {
      mbShowToast({ type: 'info', text1: 'Checkout canceled', text2: 'You have not been charged.' });
      navigation.setParams({ checkout: undefined, session_id: undefined });
    } else if (route.params?.portal) {
      MB_MobilePurchase.requestPaymentStatusRefresh();
      queryClient.invalidateQueries([USER_QUERY_KEYS.getCurrentUsertData]);
      navigation.setParams({ portal: undefined });
    }
  }, [isWeb, navigation, queryClient, route.params, syncStripeCheckoutSession]);

  function onWebPayPressed() {
    if (!selectedWebPlan) {
      return;
    }
    createStripeCheckoutSession(selectedWebPlan.productId, {
      onSuccess: url => {
        MB_customWindowForWeb.location.assign(url);
      },
      onError: error => {
        console.error('Error when creating Stripe checkout session', error);
        mbShowPopUp({
          title: 'Failed to start payment process',
          message: error.message ?? STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN,
          topComponent: MB_PopUpTemplateComponents.components.exclamationCircle,
        });
      },
    });
  }

  function onManageSubscriptionPressed() {
    createStripePortalSession(undefined, {
      onSuccess: url => {
        MB_customWindowForWeb.location.assign(url);
      },
      onError: error => {
        console.error('Error when creating Stripe portal session', error);
        mbShowToast({ type: 'error', text1: 'Could not open subscription settings', text2: error.message ?? STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN });
      },
    });
  }

  // Fetch the enabled subscriptions so we can show the stores' localized prices. Failure is
  // non-fatal — the cards fall back to generic wording and Pay Now still validates on tap.
  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }
    let isMounted = true;
    MB_MobilePurchase.getSubscriptions([...ENABLED_SUBSCRIPTION_SKUS]).then(subs => {
      if (isMounted) {
        setSubscriptions(subs);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const annualSubscription = subscriptions.find(sub => sub.id === CONSTANTS.PREMIUM_ANNUAL_SUBSCRIPTION_NAME);
  const monthlySubscription = subscriptions.find(sub => sub.id === CONSTANTS.PREMIUM_MONTHLY_SUBSCRIPTION_NAME);
  // Savings badge only makes sense when both plans are on sale.
  const annualSavingsPercent = isSinglePlan ? undefined : getAnnualSavingsPercent(annualSubscription, monthlySubscription);
  const selectedSubscription = selectedSku === CONSTANTS.PREMIUM_MONTHLY_SUBSCRIPTION_NAME ? monthlySubscription : annualSubscription;
  const selectedPeriod = getBillingPeriod(selectedSubscription) ?? (selectedSku === CONSTANTS.PREMIUM_MONTHLY_SUBSCRIPTION_NAME ? 'month' : 'year');

  const onDismissPopUpPaymentStatusModal = (shouldNavigateBack: boolean) => {
    setPaymentInProgress(false);
    if (shouldNavigateBack) {
      navigation.pop();
    }
  };

  async function validateProductId(productId: string) {
    try {
      const storeListings = await MB_MobilePurchase.getSubscriptions([productId]);
      if (storeListings.length < 1) {
        console.log('Error: Failed to find store listing with given product id of: ' + productId);
        mbShowPopUp({
          title: 'We are sorry',
          message: 'This product is not available',
          topComponent: MB_PopUpTemplateComponents.components.exclamationCircle,
        });
        return undefined;
      }

      return productId;
    } catch (error) {
      console.error('Error when validating product ID: ', error);
    }
  }

  async function onPayPressed() {
    setPaymentInProgress(true);

    const validProductId = await validateProductId(selectedSku);

    if (!validProductId) {
      setPaymentInProgress(false);
      mbShowPopUp({
        title: 'Failed to start payment process',
        message: 'Please check your internet connection and make sure you are logged in to your Apple/Google account. \n\n Then, restart the app and try again.',
        topComponent: MB_PopUpTemplateComponents.components.exclamationCircle,
      });
      return;
    }

    checkPaymentStatus(undefined, {
      onSuccess: async newPaymentStatus => {
        if (newPaymentStatus === PAYMENT_STATUS.pending || newPaymentStatus === PAYMENT_STATUS.paid) {
          mbShowPopUp({
            title: STRING_CONSTANTS.ERROR,
            message: newPaymentStatus === PAYMENT_STATUS.pending ? 'Your previous purchase is still in processing' : 'You already purchased this product',
            buttonAction: () => {
              setPaymentInProgress(false);
              mbHidePopUp();
            },
          });
          return;
        }

        await MB_MobilePurchase.requestSubscription({ sku: validProductId });
      },
      onError: error => {
        console.error('Error when checking purchase status', error);
        mbShowPopUp({
          title: 'Failed to check payment status',
          message:
            'We were unable to idendify your payment status. \n\n Please check your internet connection and make sure you are logged in to your Apple/Google account. \n\n Then, restart the app and try again.',
          topComponent: MB_PopUpTemplateComponents.components.exclamationCircle,
        });
      },
    });
  }

  const onApplyEducatorCode = () => {
    applyEducatorCode(
      { code },
      {
        onError: (error: any) => setErrorMessage(error.message ?? STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN),
      }
    );
  };

  const storeName = Platform.OS === 'ios' ? 'App Store' : 'Google Play';

  // Every outcome shows a toast so the button never appears to do nothing. The backend
  // acknowledge runs per purchase in the background; on success it emits
  // MOBILE_PURCHASE_UPDATE_SUCCESS (payment status refresh + result modal), and we also
  // ask for a refresh right away so an already-acknowledged purchase updates the UI.
  async function onRestorePressed() {
    setIsRestoring(true);
    try {
      const restoredCount = await MB_MobilePurchase.restorePurchases();
      if (restoredCount === 0) {
        mbShowToast({ type: 'info', text1: 'No purchases found', text2: `No previous purchase is tied to your ${storeName} account.` });
      } else {
        mbShowToast({ type: 'success', text1: 'Purchase found', text2: 'Restoring your premium access…' });
        MB_MobilePurchase.requestPaymentStatusRefresh();
      }
    } catch (error) {
      console.error('Error when restoring purchases', error);
      mbShowToast({ type: 'error', text1: 'Restore failed', text2: `Check your connection and ${storeName} sign-in, then try again.` });
    } finally {
      setIsRestoring(false);
    }
  }

  // if (Platform.OS === 'web') {
  //     // TODO: Address this
  //     return (
  //         <ComponentWrapper
  //             onBackPress={() => navigation.navigate('HomeNavigator', { screen: 'GameNavigator', params: { screen: 'SelectGame' } })}
  //             mobileHeaderOptions={mobileHeaderOptions.current} innerContainerStyle={{ flex: 1 }}>
  //             <View style={{ flex: 1, justifyContent: 'center' }}>
  //                 <Text style={mbTextStyles([textStyles.largestText, { marginTop: -100 }])}>Coming Soon</Text>
  //                 <Text style={mbTextStyles([textStyles.smallerText])}>Please proceed to pay on the mobile app to get premium access</Text>
  //             </View>
  //         </ComponentWrapper>
  //     );
  // }

  return (
    <ComponentWrapper
      mobileHeaderOptions={mobileHeaderOptions.current}
      onBackPress={() => navigation.navigate('HomeNavigator', { screen: 'MyAccount', params: {} })}
      backTitle="My Account"
    >
      <PaymentPageEventResponder success={educatorCode !== undefined || stripeSuccess} onDismiss={onDismissPopUpPaymentStatusModal} />

      {Platform.OS !== 'web' && (
        <View>
          <View style={styles.planRow}>
            {enabledPlans.map(plan => {
              const subscription = plan.sku === CONSTANTS.PREMIUM_MONTHLY_SUBSCRIPTION_NAME ? monthlySubscription : annualSubscription;
              const savingsPercent = plan.sku === CONSTANTS.PREMIUM_ANNUAL_SUBSCRIPTION_NAME ? annualSavingsPercent : undefined;
              const isSelected = selectedSku === plan.sku;
              const price = getLocalizedSubscriptionPrice(subscription);
              const period = getBillingPeriod(subscription) ?? plan.fallbackPeriod;
              return (
                <Pressable
                  key={plan.sku}
                  style={[styles.planPressable, isSinglePlan && styles.planPressableSingle]}
                  onPress={isSinglePlan ? undefined : () => setSelectedSku(plan.sku)}
                  disabled={isSinglePlan}
                  accessibilityRole={isSinglePlan ? 'text' : 'radio'}
                  accessibilityState={isSinglePlan ? undefined : { selected: isSelected }}
                  accessibilityLabel={`${plan.title} subscription${price ? `, ${price} per ${period}` : ''}`}
                >
                  <MB_LinearGradient
                    colors={[COLORS.backgroundPurple, COLORS.gradientLightPurple]}
                    start={{ x: 0.8, y: 0 }}
                    end={{ x: 1.5, y: 0 }}
                    style={[styles.planCard, isSelected ? styles.planCardSelected : styles.planCardUnselected]}
                  >
                    <Text style={styles.paymentTitle}>{plan.title}</Text>
                    <Text style={styles.planPrice}>{price ? `${price} / ${period}` : 'Premium access'}</Text>
                    {savingsPercent !== undefined && <Text style={styles.planSavings}>✨ Save {savingsPercent}% ✨</Text>}
                  </MB_LinearGradient>
                </Pressable>
              );
            })}
          </View>
          <Text style={styles.paymentSubtitle}>
            Auto-renews {selectedPeriod === 'month' ? 'monthly' : 'yearly'}. Cancel anytime in {Platform.OS === 'ios' ? 'the App Store' : 'Google Play'}.
          </Text>
        </View>
      )}

      {isWeb && paymentStatus !== PAYMENT_STATUS.paid && (
        <View>
          {isStripePlansLoading && <Text style={styles.paymentSubtitle}>Loading plans…</Text>}
          {isStripePlansError && <Text style={styles.paymentSubtitle}>Online payment is not available right now. You can still subscribe from the mobile app.</Text>}
          {webPlans.length > 0 && (
            <>
              <View style={styles.planRow}>
                {webPlans.map(plan => {
                  const isSelected = selectedWebPlan?.productId === plan.productId;
                  const savingsPercent = plan.productId === CONSTANTS.PREMIUM_ANNUAL_SUBSCRIPTION_NAME ? webAnnualSavingsPercent : undefined;
                  return (
                    <Pressable
                      key={plan.productId}
                      style={[styles.planPressable, isSingleWebPlan && styles.planPressableSingle]}
                      onPress={isSingleWebPlan ? undefined : () => setSelectedSku(plan.productId)}
                      disabled={isSingleWebPlan}
                      accessibilityRole={isSingleWebPlan ? 'text' : 'radio'}
                      accessibilityState={isSingleWebPlan ? undefined : { selected: isSelected }}
                      accessibilityLabel={`${plan.title} subscription, ${formatStripePrice(plan)} per ${plan.interval}`}
                      testID={`web-plan-${plan.productId}`}
                    >
                      <MB_LinearGradient
                        colors={[COLORS.backgroundPurple, COLORS.gradientLightPurple]}
                        start={{ x: 0.8, y: 0 }}
                        end={{ x: 1.5, y: 0 }}
                        style={[styles.planCard, isSelected ? styles.planCardSelected : styles.planCardUnselected]}
                      >
                        <Text style={styles.paymentTitle}>{plan.title}</Text>
                        <Text style={styles.planPrice}>{`${formatStripePrice(plan)} / ${plan.interval}`}</Text>
                        {savingsPercent !== undefined && <Text style={styles.planSavings}>✨ Save {savingsPercent}% ✨</Text>}
                      </MB_LinearGradient>
                    </Pressable>
                  );
                })}
              </View>
              <Text style={styles.paymentSubtitle}>
                Auto-renews {selectedWebPlan?.interval === 'month' ? 'monthly' : 'yearly'}. Secure checkout by Stripe. Cancel anytime from Manage subscription.
              </Text>
            </>
          )}
        </View>
      )}

      <View style={styles.educatorCode}>
        <Text style={styles.educatorCodeText}>Redeem your code</Text>
        <View style={styles.educatorCodeRow}>
          <View style={styles.educatorCodeInputConatiner}>
            <MB_TextInput
              style={[styles.educatorCodeInput, Boolean(errorMessage) && styles.educatorCodeInputError]}
              placeholder="Enter redeem code"
              placeholderTextColor="#C2C2C2"
              value={code}
              onChangeText={setCode}
            />
          </View>
          <MB_Button
            style={styles.educatorCodeButton}
            rightElement={<Feather name="arrow-right" color="#FFFFFF" size={25} />}
            rightElementStyle={{ marginLeft: -2 }}
            disabled={isApplyingCode || !code}
            loading={isApplyingCode}
            onPress={onApplyEducatorCode}
          />
        </View>
        <Text style={styles.errorMessageText}>{errorMessage ?? ' '}</Text>
      </View>
      {Platform.OS !== 'web' && (
        <View style={{ marginTop: 'auto' }}>
          {paymentStatus === PAYMENT_STATUS.pending && <Text style={textStyles.popUpErrorMessageText}>Warning: Previous payment is still in processing, please wait.</Text>}

          <MB_Button
            onPress={onPayPressed}
            title="Pay Now"
            style={styles.buttonContainer}
            textStyle={styles.buttonText}
            disabled={isCheckPurchaseStatusLoading || paymentInProgress || paymentStatus === PAYMENT_STATUS.pending || paymentStatus === PAYMENT_STATUS.paid}
            loading={(isCheckPurchaseStatusLoading || paymentInProgress) && paymentStatus !== PAYMENT_STATUS.pending && paymentStatus !== PAYMENT_STATUS.paid}
          />

          <MB_Button
            onPress={onRestorePressed}
            title="Restore Purchase"
            style={styles.restoreButton}
            textStyle={styles.restoreButtonText}
            disabled={isRestoring || paymentInProgress || paymentStatus === PAYMENT_STATUS.paid}
            loading={isRestoring}
          />
        </View>
      )}
      {isWeb && (
        <View style={{ marginTop: 'auto' }}>
          {paymentStatus === PAYMENT_STATUS.pending && <Text style={textStyles.popUpErrorMessageText}>Warning: Previous payment is still in processing, please wait.</Text>}
          {paymentStatus !== PAYMENT_STATUS.paid && webPlans.length > 0 && (
            <MB_Button
              onPress={onWebPayPressed}
              title="Pay Now"
              style={styles.buttonContainer}
              textStyle={styles.buttonText}
              disabled={isCreatingCheckout || isSyncingCheckout || paymentStatus === PAYMENT_STATUS.pending}
              loading={isCreatingCheckout || isSyncingCheckout}
            />
          )}
          {isStripeSubscriber && (
            <MB_Button
              onPress={onManageSubscriptionPressed}
              title="Manage subscription"
              style={styles.restoreButton}
              textStyle={styles.restoreButtonText}
              disabled={isCreatingPortal}
              loading={isCreatingPortal}
            />
          )}
        </View>
      )}
    </ComponentWrapper>
  );
};

export default PaymentPage;

const styles = StyleSheet.create({
  buttonContainer: {
    width: '80%',
    alignSelf: 'center',
    backgroundColor: COLORS.gradientDarkPurple,
    marginTop: 16,
  },
  planRow: {
    flexDirection: 'row',
    marginTop: 16,
    gap: 10,
  },
  planPressable: {
    flex: 1,
  },
  planPressableSingle: {
    maxWidth: 360,
    alignSelf: 'center',
  },
  planCard: {
    flex: 1,
    borderRadius: 16,
    padding: 16,
    borderWidth: 2,
  },
  planCardSelected: {
    borderColor: '#A430FF',
  },
  planCardUnselected: {
    borderColor: '#412366',
  },
  paymentTitle: mbTextStyles([
    textStyles.normalText,
    {
      textAlign: 'left',
      fontWeight: '600',
    },
  ]),
  planPrice: mbTextStyles([
    textStyles.normalText,
    {
      textAlign: 'left',
      fontWeight: '600',
      marginTop: 4,
    },
  ]),
  planSavings: mbTextStyles([
    textStyles.smallerText,
    {
      textAlign: 'left',
      marginTop: 6,
      fontWeight: '600',
      color: COLORS.yellowButton,
    },
  ]),
  paymentSubtitle: mbTextStyles([
    textStyles.smallerText,
    {
      textAlign: 'left',
      marginTop: 8,
      color: COLORS.textLightPurple,
    },
  ]),
  payNowContainer: {
    borderRadius: 12,
    paddingVertical: 16,
  },
  restoreButton: {
    width: '80%',
    alignSelf: 'center',
    backgroundColor: 'transparent',
    marginTop: 8,
  },
  restoreButtonText: mbTextStyles([
    textStyles.smallText,
    {
      fontSize: 14,
      textAlign: 'center',
      fontWeight: '400',
      textDecorationLine: 'underline',
    },
  ]),
  buttonText: mbTextStyles([
    textStyles.normalText,
    {
      fontSize: 16,
      textAlign: 'center',
      fontWeight: '600',
    },
  ]),

  // educatorCode

  educatorCode: {
    borderWidth: 1,
    borderColor: '#412366',
    padding: 10,
    borderRadius: 7,
    marginTop: 15,
  },
  educatorCodeText: mbTextStyles([
    textStyles.normalText,
    {
      textAlign: 'left',
    },
  ]),
  educatorCodeRow: {
    flexDirection: 'row',
    marginTop: 12,
    alignItems: 'center',
  },
  educatorCodeInputConatiner: {
    flex: 1,
  },
  educatorCodeInput: {
    flexGrow: 1,
    width: 'auto',
    alignSelf: 'stretch',
    height: 48,
    backgroundColor: '#220133',
    borderColor: '#220133',
    borderWidth: 1,
  },
  educatorCodeInputError: {
    borderColor: '#E53232',
  },
  educatorCodeButton: {
    width: 54,
    height: 48,
    backgroundColor: '#A430FF',
    borderRadius: 10,
    marginLeft: 5,
  },
  errorMessageText: mbTextStyles([
    textStyles.smallerText,
    {
      marginTop: 8,
      textAlign: 'left',
      fontWeight: '500',
      color: '#E53232',
    },
  ]),
});
